import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { 
  Building, Trash2, CheckCircle, Filter, Plus, ChevronDown, ChevronUp,
  Search, User, Phone, Mail, Globe, MapPin, Award, FileText, Check, 
  X, Eye, Pin, GraduationCap, DollarSign, Handshake
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const InstitutionDetailDrawer = ({ institution, onClose, onUpdateStatus }) => {
  if (!institution) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 08 &bull; Chartered Academic Partner
              </span>
              <span className="text-xs font-mono font-bold text-amber-400">
                {institution.institutionType || 'University'}
              </span>
            </div>
            <h2 className="text-2xl font-black text-white">{institution.name}</h2>
            <p className="text-xs text-zinc-400 font-bold mt-1 flex items-center gap-2">
              <Building size={14} className="text-rose-400" /> {institution.city || 'Nairobi'}, {institution.county || 'Kenya'} &bull; {institution.accreditationCharter || 'Chartered Institution'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {institution.status || 'Active'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Student Volume</span>
              <span className="text-xs font-bold text-white font-mono">
                {(institution.studentVolume || institution.academic?.studentVolume || 0).toLocaleString()} Students
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">DIS Quota</span>
              <span className="text-xs font-bold text-rose-400 font-mono">
                {institution.studentQuota || 150} Placements / yr
              </span>
            </div>
          </div>

          {/* Section 1: Institution Profile */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Building size={14} /> Legal Charter &amp; Accreditation
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Institution Legal Name:</span>
                <span className="text-zinc-200 font-bold">{institution.name}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Institution Type:</span>
                <span className="text-zinc-200">{institution.institutionType || 'Chartered University'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Charter / Reg No:</span>
                <span className="text-zinc-200 font-mono">{institution.accreditationCharter || institution.charterNo || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">KRA PIN Number:</span>
                <span className="text-zinc-200 font-mono">{institution.kraPinNumber || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Accreditation Body:</span>
                <span className="text-zinc-200">{institution.accreditationBody || institution.academic?.accreditationBody || 'CUE Accredited'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Website:</span>
                <span className="text-rose-300 font-mono">{institution.officialWebsite || institution.website || '-'}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Executive Leadership */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> Executive Leadership &amp; Registry Contacts
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Vice Chancellor / President:</span>
                <span className="text-zinc-200 font-bold">{institution.vcName || institution.directorName || institution.contacts?.director?.name || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">VC Office Email:</span>
                <span className="text-zinc-200 font-mono">{institution.vcEmail || institution.directorEmail || institution.contacts?.director?.email || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">VC Direct Phone:</span>
                <span className="text-zinc-200 font-mono">{institution.vcPhone || institution.phone || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Academic Registrar:</span>
                <span className="text-zinc-200 font-bold">{institution.registrarName || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Registrar Email:</span>
                <span className="text-zinc-200 font-mono">{institution.registrarEmail || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Registrar Phone:</span>
                <span className="text-zinc-200 font-mono">{institution.registrarPhone || '-'}</span>
              </div>
            </div>
          </div>

          {/* Section 3: Partnership & Revenue Terms */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Handshake size={14} /> DIS Partnership Mandate &amp; Revenue Sharing
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Partnership Scope:</span>
                <span className="text-zinc-200 font-bold">{institution.partnershipScope || 'Joint Degree Certification'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Annual Cohort Quota:</span>
                <span className="text-rose-400 font-mono font-bold">{institution.studentQuota || 150} Students</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Revenue Share Terms:</span>
                <span className="text-amber-400 font-mono font-bold">{institution.revenueSharePercent || 20}% Gross Tuition</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Scholarship Quota:</span>
                <span className="text-zinc-200 font-mono">{institution.scholarshipQuota || 15} Reserved</span>
              </div>
            </div>

            {institution.mouDocketUrl && (
              <div className="pt-2 border-t border-zinc-800">
                <a href={institution.mouDocketUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg inline-flex items-center gap-1.5 font-bold">
                  <FileText size={12} /> View Executed MoU Docket
                </a>
              </div>
            )}
          </div>

          {/* Admin Status Switcher */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Charter Status</span>
            <div className="flex gap-2">
              <button 
                onClick={() => onUpdateStatus(institution.id, 'Active')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${institution.status === 'Active' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Active
              </button>
              <button 
                onClick={() => onUpdateStatus(institution.id, 'Reviewing MoU')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${institution.status === 'Reviewing MoU' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Reviewing MoU
              </button>
              <button 
                onClick={() => onUpdateStatus(institution.id, 'Inactive')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${institution.status === 'Inactive' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const InstitutionManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedInstitution, setSelectedInstitution] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('name');

    // Comprehensive Registration Form State (Matching 100% of Academic Partner Onboarding)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [name, setName] = useState('');
    const [institutionType, setInstitutionType] = useState('Chartered University');
    const [accreditationCharter, setAccreditationCharter] = useState('CUE/UNIV/2012/04');
    const [kraPinNumber, setKraPinNumber] = useState('');
    const [officialWebsite, setOfficialWebsite] = useState('');
    const [logoUrl, setLogoUrl] = useState('');

    const [vcName, setVcName] = useState('');
    const [vcEmail, setVcEmail] = useState('');
    const [vcPhone, setVcPhone] = useState('');
    const [registrarName, setRegistrarName] = useState('');
    const [registrarEmail, setRegistrarEmail] = useState('');
    const [registrarPhone, setRegistrarPhone] = useState('');

    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [county, setCounty] = useState('Nairobi');
    const [country, setCountry] = useState('Kenya');
    const [studentVolume, setStudentVolume] = useState(15000);
    const [facultyCount, setFacultyCount] = useState(8);
    const [graduationRate, setGraduationRate] = useState(88);

    const [partnershipScope, setPartnershipScope] = useState('Joint Degree Certification');
    const [studentQuota, setStudentQuota] = useState(250);
    const [revenueSharePercent, setRevenueSharePercent] = useState(20);
    const [scholarshipQuota, setScholarshipQuota] = useState(20);
    const [accreditationBody, setAccreditationBody] = useState('Commission for University Education (CUE)');
    const [mouDocketUrl, setMouDocketUrl] = useState('');

    const fetchInstitutions = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'institutions'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
                data.unshift(
                  {
                    id: 'teau-university',
                    name: 'The East African University',
                    institutionType: 'Chartered University',
                    accreditationCharter: 'CUE/UNIV/2012/08',
                    kraPinNumber: 'P051188339K',
                    officialWebsite: 'https://teau.ac.ke',
                    vcName: 'Prof. Christopher Kimani',
                    vcEmail: 'vc@teau.ac.ke',
                    vcPhone: '+254 722 000 333',
                    registrarName: 'Dr. Evans Omondi',
                    registrarEmail: 'registrar@teau.ac.ke',
                    registrarPhone: '+254 722 111 222',
                    address: 'Namanga Highway, Kitengela Complex',
                    city: 'Kitengela',
                    county: 'Kajiado',
                    country: 'Kenya',
                    studentVolume: 12500,
                    facultyCount: 6,
                    graduationRate: 92,
                    partnershipScope: 'Joint Degree & Blended Technical Campus Workshops',
                    studentQuota: 300,
                    revenueSharePercent: 20,
                    scholarshipQuota: 25,
                    accreditationBody: 'Commission for University Education (CUE)',
                    status: 'Active',
                    addedAt: '2026-06-01T08:00:00Z'
                  },
                  {
                    id: 'suny-potsdam',
                    name: 'SUNY Potsdam, USA',
                    institutionType: 'International University',
                    accreditationCharter: 'NYSED/US/1816/001',
                    kraPinNumber: 'EXEMPT-INTL-ACAD',
                    officialWebsite: 'https://www.potsdam.edu',
                    vcName: 'Dr. Suzanne Smith',
                    vcEmail: 'president@potsdam.edu',
                    vcPhone: '+1 315 267 2000',
                    registrarName: 'Office of International Education',
                    registrarEmail: 'international@potsdam.edu',
                    registrarPhone: '+1 315 267 2188',
                    address: '44 Pierrepont Avenue, Potsdam, NY 13676',
                    city: 'Potsdam',
                    county: 'New York',
                    country: 'United States',
                    studentVolume: 3500,
                    facultyCount: 14,
                    graduationRate: 96,
                    partnershipScope: 'Direct Credit Articulation & Global Dual Certifications',
                    studentQuota: 500,
                    revenueSharePercent: 25,
                    scholarshipQuota: 40,
                    accreditationBody: 'Middle States Commission on Higher Education (MSCHE)',
                    status: 'Active',
                    addedAt: '2026-06-15T12:00:00Z'
                  }
                );
            }
            setInstitutions(data);
        } catch (e) {
            console.error('Error fetching institutions:', e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchInstitutions();
    }, [showDemo]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setStatusMessage('saving');

        const institutionId = name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

        try {
            await setDoc(doc(db, 'institutions', institutionId), {
                name: name.trim(),
                institutionType,
                accreditationCharter: accreditationCharter.trim(),
                kraPinNumber: kraPinNumber.trim(),
                officialWebsite: officialWebsite.trim(),
                website: officialWebsite.trim(),
                logoUrl: logoUrl.trim(),
                vcName: vcName.trim(),
                directorName: vcName.trim(),
                vcEmail: vcEmail.trim().toLowerCase(),
                directorEmail: vcEmail.trim().toLowerCase(),
                email: vcEmail.trim().toLowerCase(),
                vcPhone: vcPhone.trim(),
                phone: vcPhone.trim(),
                registrarName: registrarName.trim(),
                registrarEmail: registrarEmail.trim().toLowerCase(),
                registrarPhone: registrarPhone.trim(),
                address: address.trim(),
                city: city.trim(),
                county: county.trim(),
                country: country.trim(),
                studentVolume: parseInt(studentVolume) || 0,
                facultyCount: parseInt(facultyCount) || 0,
                graduationRate: parseInt(graduationRate) || 0,
                academic: {
                    studentVolume: parseInt(studentVolume) || 0,
                    accreditationBody: accreditationBody.trim(),
                    graduationRate: parseInt(graduationRate) || 0
                },
                contacts: {
                    director: {
                        name: vcName.trim(),
                        email: vcEmail.trim().toLowerCase()
                    }
                },
                partnershipScope,
                studentQuota: parseInt(studentQuota) || 0,
                revenueSharePercent: parseInt(revenueSharePercent) || 0,
                scholarshipQuota: parseInt(scholarshipQuota) || 0,
                accreditationBody: accreditationBody.trim(),
                mouDocketUrl: mouDocketUrl.trim(),
                status: 'Active',
                updatedAt: new Date().toISOString(),
                addedAt: new Date().toISOString()
            }, { merge: true });

            setName('');
            setAccreditationCharter('');
            setKraPinNumber('');
            setOfficialWebsite('');
            setVcName('');
            setVcEmail('');
            setVcPhone('');
            setRegistrarName('');
            setAddress('');
            setCity('');
            setIsFormOpen(false);

            setStatusMessage('success');
            fetchInstitutions();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error('Error adding institution:', e);
            setStatusMessage('error');
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
      try {
        await setDoc(doc(db, 'institutions', id), {
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        }, { merge: true });
        
        setInstitutions(prev => prev.map(inst => inst.id === id ? { ...inst, status: newStatus } : inst));
        if (selectedInstitution && selectedInstitution.id === id) {
          setSelectedInstitution(prev => ({ ...prev, status: newStatus }));
        }
      } catch (err) {
        console.error("Failed to update institution status:", err);
      }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this institution?')) return;
        try {
            await deleteDoc(doc(db, 'institutions', id));
            if (selectedInstitution?.id === id) setSelectedInstitution(null);
            fetchInstitutions();
        } catch (e) {
            console.error(e);
        }
    };

    const filteredInstitutions = institutions.filter(inst => {
      const matchesType = filterType === 'all' || (inst.institutionType || '').toLowerCase() === filterType.toLowerCase();
      const query = searchTerm.toLowerCase();
      const iName = (inst.name || '').toLowerCase();
      const iLoc = (inst.city || inst.county || '').toLowerCase();
      const iVc = (inst.vcName || inst.directorName || '').toLowerCase();
      const iCharter = (inst.accreditationCharter || '').toLowerCase();
      const matchesSearch = !searchTerm || iName.includes(query) || iLoc.includes(query) || iVc.includes(query) || iCharter.includes(query);
      return matchesType && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedInstitutions = [...filteredInstitutions].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-rose-500/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-xl">
                                <Building size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Portal 08 &bull; Academic Partnerships
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">University &amp; Institutional Registry</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            Chartered universities, national polytechnics, TVET institutes, and formal credit articulation partner institutions.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-rose-600 hover:bg-rose-700 text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Building size={16} /> {isFormOpen ? 'Close Form' : 'Register Academic Partner'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Registration Form */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-rose-400 flex items-center gap-2">
                              <GraduationCap size={18} /> Register Institutional Partner &bull; Full Charter &amp; Mandate
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Record executive governance, legal charters, DIS intake quotas, revenue sharing terms, and MoU dockets.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleAdd} className="space-y-6">
                      {/* Section 1: Institutional Profile & Charter */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 flex items-center gap-2">
                              <Building size={14} /> 1. Institutional Identity &amp; Legal Charter
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Institution Legal Name *</label>
                                  <input 
                                      type="text" 
                                      value={name} 
                                      onChange={e => setName(e.target.value)} 
                                      placeholder="e.g. The East African University / SUNY Potsdam" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-rose-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Institution Classification</label>
                                  <select 
                                      value={institutionType} 
                                      onChange={e => setInstitutionType(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-rose-500 font-bold"
                                  >
                                      <option>Chartered University</option>
                                      <option>Constituent University College</option>
                                      <option>International University</option>
                                      <option>National Polytechnic</option>
                                      <option>TVET Technical Institute</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Charter / Legal Reg No *</label>
                                  <input 
                                      type="text" 
                                      value={accreditationCharter} 
                                      onChange={e => setAccreditationCharter(e.target.value)} 
                                      placeholder="e.g. CUE/UNIV/2012/08" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-rose-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">KRA PIN Number</label>
                                  <input 
                                      type="text" 
                                      value={kraPinNumber} 
                                      onChange={e => setKraPinNumber(e.target.value)} 
                                      placeholder="e.g. P051188339K" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Website URL</label>
                                  <input 
                                      type="url" 
                                      value={officialWebsite} 
                                      onChange={e => setOfficialWebsite(e.target.value)} 
                                      placeholder="https://teau.ac.ke" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Accrediting Authority</label>
                                  <input 
                                      type="text" 
                                      value={accreditationBody} 
                                      onChange={e => setAccreditationBody(e.target.value)} 
                                      placeholder="e.g. CUE / TVETA / MSCHE" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Executive Governance */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 flex items-center gap-2">
                              <User size={14} /> 2. Vice Chancellor &amp; Academic Registry Leadership
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Vice Chancellor / President Name *</label>
                                  <input 
                                      type="text" 
                                      value={vcName} 
                                      onChange={e => setVcName(e.target.value)} 
                                      placeholder="e.g. Prof. Christopher Kimani" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-rose-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">VC Office Email *</label>
                                  <input 
                                      type="email" 
                                      value={vcEmail} 
                                      onChange={e => setVcEmail(e.target.value)} 
                                      placeholder="vc@teau.ac.ke" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-rose-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">VC Phone *</label>
                                  <input 
                                      type="tel" 
                                      value={vcPhone} 
                                      onChange={e => setVcPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-rose-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Academic Registrar Name</label>
                                  <input 
                                      type="text" 
                                      value={registrarName} 
                                      onChange={e => setRegistrarName(e.target.value)} 
                                      placeholder="e.g. Dr. Evans Omondi" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Registrar Email</label>
                                  <input 
                                      type="email" 
                                      value={registrarEmail} 
                                      onChange={e => setRegistrarEmail(e.target.value)} 
                                      placeholder="registrar@teau.ac.ke" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Registrar Direct Phone</label>
                                  <input 
                                      type="tel" 
                                      value={registrarPhone} 
                                      onChange={e => setRegistrarPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Physical Footprint & Population */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 flex items-center gap-2">
                              <MapPin size={14} /> 3. Campus Location &amp; Student Body Demographics
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Main Campus Address</label>
                                  <input 
                                      type="text" 
                                      value={address} 
                                      onChange={e => setAddress(e.target.value)} 
                                      placeholder="e.g. Kitengela Complex, Main Gate" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">City / Town</label>
                                  <input 
                                      type="text" 
                                      value={city} 
                                      onChange={e => setCity(e.target.value)} 
                                      placeholder="e.g. Kitengela / Nairobi" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">County / Country</label>
                                  <input 
                                      type="text" 
                                      value={county} 
                                      onChange={e => setCounty(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Total Student Population</label>
                                  <input 
                                      type="number" 
                                      value={studentVolume} 
                                      onChange={e => setStudentVolume(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500 font-mono"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Faculties / Schools Count</label>
                                  <input 
                                      type="number" 
                                      value={facultyCount} 
                                      onChange={e => setFacultyCount(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500 font-mono"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Annual Graduation Rate (%)</label>
                                  <input 
                                      type="number" 
                                      value={graduationRate} 
                                      onChange={e => setGraduationRate(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500 font-mono"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 4: DIS Partnership Mandate */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-rose-400 flex items-center gap-2">
                              <Handshake size={14} /> 4. DIS Partnership Mandate, Quota &amp; Revenue Share
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Partnership Scope</label>
                                  <select 
                                      value={partnershipScope} 
                                      onChange={e => setPartnershipScope(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500 font-bold"
                                  >
                                      <option>Joint Degree Certification</option>
                                      <option>Direct Credit Transfer & Dual Award</option>
                                      <option>Blended Practical Campus Workshops</option>
                                      <option>Co-Designed Curriculum & Faculty Exchange</option>
                                      <option>Exclusive Regional Accreditation Partner</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Annual DIS Student Quota</label>
                                  <input 
                                      type="number" 
                                      value={studentQuota} 
                                      onChange={e => setStudentQuota(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-rose-400 font-mono font-bold text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Revenue Share Agreement (%)</label>
                                  <input 
                                      type="number" 
                                      value={revenueSharePercent} 
                                      onChange={e => setRevenueSharePercent(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-amber-400 font-mono font-bold text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Executed MoU Docket URL</label>
                                  <input 
                                      type="url" 
                                      value={mouDocketUrl} 
                                      onChange={e => setMouDocketUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Reserved Scholarship Quota</label>
                                  <input 
                                      type="number" 
                                      value={scholarshipQuota} 
                                      onChange={e => setScholarshipQuota(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono text-xs focus:outline-none focus:border-rose-500"
                                  />
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-rose-600 hover:bg-rose-700 text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Registering...' : 'Register Academic Partner'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> Academic institution partner registered successfully.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by university name, VC, registrar, city, or charter..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Classifications</option>
                            <option value="chartered university">Chartered University</option>
                            <option value="constituent university college">Constituent College</option>
                            <option value="international university">International</option>
                            <option value="national polytechnic">National Polytechnic</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchInstitutions}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>

                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Institution Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'name' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-rose-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Institution Name</span> {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'name' ? 'none' : 'name')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'name' ? 'text-rose-400' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'name' ? 'Unpin column' : 'Pin Institution Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'name' ? 'fill-rose-400' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Classification */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Type
                              </th>

                              {/* Charter / Reg */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Charter / Reg No
                              </th>

                              {/* Location */}
                              <th className="min-w-[150px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                City &amp; County
                              </th>

                              {/* Vice Chancellor */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Vice Chancellor
                              </th>

                              {/* VC Contact */}
                              <th className="min-w-[170px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                VC Email &amp; Phone
                              </th>

                              {/* Academic Registrar */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Academic Registrar
                              </th>

                              {/* Student Volume */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Total Enrolled
                              </th>

                              {/* DIS Intake Quota */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                DIS Quota
                              </th>

                              {/* Partnership Scope */}
                              <th className="min-w-[200px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Partnership Scope
                              </th>

                              {/* Revenue Share */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Rev Share %
                              </th>

                              {/* Accreditation */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Accreditation Body
                              </th>

                              {/* Status */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="15" className="p-8 text-center text-zinc-500 text-xs">Loading Academic Institutions...</td></tr>
                          ) : sortedInstitutions.length === 0 ? (
                              <tr><td colSpan="15" className="p-8 text-center text-zinc-500 text-xs">No institutions found matching criteria.</td></tr>
                          ) : sortedInstitutions.map((inst, idx) => {
                              return (
                                <tr 
                                    key={inst.id} 
                                    onClick={() => setSelectedInstitution(inst)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'name' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-rose-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <p className="font-bold text-white group-hover:text-rose-400 transition-colors">{inst.name}</p>
                                        <p className="text-[10px] text-zinc-500 font-mono">{inst.officialWebsite || inst.website || '-'}</p>
                                    </td>

                                    {/* Classification */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {inst.institutionType || 'University'}
                                    </td>

                                    {/* Charter */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-400">
                                        {inst.accreditationCharter || inst.charterNo || '-'}
                                    </td>

                                    {/* Location */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300 font-semibold">
                                        {inst.city || 'Nairobi'}, {inst.county || 'Kenya'}
                                    </td>

                                    {/* VC */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{inst.vcName || inst.directorName || inst.contacts?.director?.name || '-'}</p>
                                        <p className="text-[10px] text-zinc-500">Chief Executive</p>
                                    </td>

                                    {/* VC Contact */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px]">
                                        <p className="text-zinc-300">{inst.vcEmail || inst.directorEmail || inst.contacts?.director?.email || '-'}</p>
                                        <p className="text-zinc-500">{inst.vcPhone || inst.phone || '-'}</p>
                                    </td>

                                    {/* Registrar */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{inst.registrarName || '-'}</p>
                                        <p className="font-mono text-[10px] text-zinc-500">{inst.registrarEmail || '-'}</p>
                                    </td>

                                    {/* Student Volume */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-white font-bold">
                                        {(inst.studentVolume || inst.academic?.studentVolume || 0).toLocaleString()}
                                    </td>

                                    {/* DIS Quota */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-rose-400 font-bold">
                                        {inst.studentQuota || 150} Seats
                                    </td>

                                    {/* Scope */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300 max-w-[200px] truncate" title={inst.partnershipScope}>
                                        {inst.partnershipScope || 'Joint Degree'}
                                    </td>

                                    {/* Revenue Share */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-amber-400 font-bold">
                                        {inst.revenueSharePercent || 20}%
                                    </td>

                                    {/* Accreditation */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400">
                                        {inst.accreditationBody || inst.academic?.accreditationBody || 'Accredited'}
                                    </td>

                                    {/* Status */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          inst.status === 'Active' 
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {inst.status || 'Active'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedInstitution(inst)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Institution Dossier"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(inst.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Institution"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <InstitutionDetailDrawer 
              institution={selectedInstitution} 
              onClose={() => setSelectedInstitution(null)} 
              onUpdateStatus={handleUpdateStatus}
            />
        </div>
    );
};

export default InstitutionManager;
